import { ComponentProps, forwardRef, useCallback } from 'react'
import { useDrag, useDrop } from 'react-dnd'

import { BusinessModelEnum } from '@/entities/user/models/BusinessModel'
import { widgetWindowsSelector } from '@/entities/widgetWindows'
import { GridTableDragItem, GridTableDragType } from '@/shared/components/GridTable/models/GridTableDragType'
import { MosaicDragItem, MosaicDragType, WidgetType, WidgetTypeWithSuffix } from '@/shared/components/Mosaic'
import { isTreeWidgetType } from '@/shared/components/Mosaic/lib/isTreeWidgetType'
import { TREE_WIDGETS } from '@/shared/components/Mosaic/models/widgetType'
import { MOSAIC_LAYOUT_ID } from '@/shared/config/consts'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { Collapse, styled } from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import { TreeItem2, TreeItem2Content, TreeItem2IconContainer, TreeItem2Root } from '@mui/x-tree-view/TreeItem2'
import { TreeItem2Icon } from '@mui/x-tree-view/TreeItem2Icon'
import { TreeItem2Provider } from '@mui/x-tree-view/TreeItem2Provider'
import { useTreeItem2 } from '@mui/x-tree-view/useTreeItem2/useTreeItem2'
import { animated, useSpring } from '@react-spring/web'

import { useMosaicLayoutContext } from '../MosaicLayout/MosaicLayoutContext'
import { getIconFromNodeType } from './lib/getIconFromNodeType'
import { isExpandable } from './lib/isExpandable'
import { processDropOnTreeItem } from './lib/processDropOnTreeItem'
import { CustomTreeItemType } from './model/CustomTreeItemType'
import { TreeLabel } from './TreeLabel'

export type CustomTreeItemProps = Omit<ComponentProps<typeof TreeItem2>, 'rootRef'> & {
  indentationPadding?: number
}

export const CustomTreeItem: React.FC<CustomTreeItemProps> = forwardRef((props, ref) => {
  const { id, itemId, label, disabled, children, ...other } = props
  const {
    getRootProps,
    getContentProps,
    getIconContainerProps,
    getLabelProps,
    getGroupTransitionProps,
    status,
    publicAPI,
  } = useTreeItem2({ id, itemId, children, label, disabled, rootRef: ref })

  const { replaceWidgetWindow } = useMosaicLayoutContext()
  const activeWidgetWindowId = useAppSelector(state => state.widgetWindows.activeWidgetWindowId)
  const businessModel = useAppSelector(state => state.user.businessModel)
  const currentScreen = useAppSelector(state => state.userInterface.currentScreen)
  const isCourierModel = businessModel === BusinessModelEnum.Courier
  const treeItem: CustomTreeItemType = publicAPI.getItem(itemId)
  const widgetKey: WidgetTypeWithSuffix = `${treeItem.widgetType}-${itemId}`
  const isSelected = useAppSelector(state => widgetWindowsSelector.selectById(state, widgetKey)?.isActive) || false
  const dispatch = useAppDispatch()

  const expandable = isExpandable(children)

  // Get the icon for the tree item
  let icon

  if (expandable) {
    icon = null
  } else if (treeItem.widgetType) {
    icon = getIconFromNodeType(treeItem.widgetType, treeItem.uiColor, isCourierModel)
  }

  const [_, dragRef] = useDrag(() => {
    const dragItem: MosaicDragItem = {
      mosaicId: MOSAIC_LAYOUT_ID,
      type: MosaicDragType.TREE_ITEM,
      itemId: `${treeItem.widgetType}-${itemId}`,
      widgetType: treeItem.widgetType,
    }

    return {
      type: MosaicDragType.TREE_ITEM,
      canDrag: treeItem.isDraggable,
      item: dragItem,
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }
  }, [treeItem])

  const [{ isOver }, dropRef] = useDrop({
    accept: treeItem.acceptDropTypes,
    drop: (item: GridTableDragItem, monitor) => {
      const didDrop = monitor.didDrop()

      if (didDrop) return

      processDropOnTreeItem({
        dispatch,
        item,
        treeItem,
        currentScreen,
      })
    },
    collect: (monitor) => {
      const item = monitor.getItem() as GridTableDragItem
      let isDroppable = true

      if (item?.rows && item.rows.length > 0 &&
        item.type === GridTableDragType.UNASSIGNED_ORDER_ROW &&
        treeItem.widgetType === TREE_WIDGETS.ScenarioUnassignedOrders &&
        item.rows[0].scenarioId == treeItem.scenarioId
      ) {
        isDroppable = false
      }

      if (item?.rows && item.rows.length > 0 &&
        item.type === GridTableDragType.SHIFT_STOP_ROW &&
        (treeItem.widgetType === TREE_WIDGETS.PlanningShiftTable ||
          treeItem.widgetType === TREE_WIDGETS.RunsheetShiftTable) &&
        item.rows[0].shiftId == treeItem.shiftId
      ) {
        isDroppable = false
      }

      return {
        isOver: !!monitor.isOver({ shallow: true }) && isDroppable,
      }
    },
  }, [treeItem, currentScreen])

  // Open the widget window when the tree item is clicked
  const onTreeItemClicked = useCallback(() => {
    const activeTreeWindowWidgetType = (activeWidgetWindowId as WidgetTypeWithSuffix).split('-')?.[0] as WidgetType

    if (!isSelected && activeWidgetWindowId && isTreeWidgetType(activeTreeWindowWidgetType)) {
      replaceWidgetWindow(activeWidgetWindowId, widgetKey)
    }
  }, [activeWidgetWindowId, isSelected, replaceWidgetWindow, widgetKey])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const otherProps = getRootProps(other) as any
  const isSummaryTreeWidget = treeItem.widgetType === TREE_WIDGETS.ScenarioSummaryTable || treeItem.widgetType === TREE_WIDGETS.RunsheetSummaryTable

  return (
    <TreeItem2Provider itemId={itemId.toString()}>
      <StyledTreeItemRoot
        {...otherProps}
      >
        <CustomTreeItemContent
          {...getContentProps()}
          ref={(node) => dragRef(dropRef(node))}
          $isSelected={isSelected}
          $isOver={isOver}
          {...!isSummaryTreeWidget && { onClick: onTreeItemClicked }}
        >
          {expandable && <TreeItem2IconContainer {...getIconContainerProps()}>
            <TreeItem2Icon status={status} />
          </TreeItem2IconContainer>}

          <TreeLabel
            {...getLabelProps({ icon, expandable: expandable && status.expanded })}
            treeItem={treeItem}
          />
        </CustomTreeItemContent>
        {children && <TransitionComponent {...getGroupTransitionProps()} />}
      </StyledTreeItemRoot>
    </TreeItem2Provider>
  )
})

const StyledTreeItemRoot = styled(TreeItem2Root)`
  & .MuiCollapse-root{
    padding-left: 0 !important;
  }
`

const CustomTreeItemContent = styled(TreeItem2Content, {
  shouldForwardProp: (prop) => prop !== '$isSelected' && prop !== '$isOver',
}) <{ $isSelected: boolean, $isOver: boolean }>`
  border-radius: 0;
  paddingLeft: ${({ theme }) => `calc(${theme.spacing(1)} + var(--TreeView-itemChildrenIndentation) * var(--TreeView-itemDepth))`}
  color: ${({ theme }) => theme.palette.text.dim};
  overflow: visible;
  
  ${({ $isSelected, theme }) => $isSelected && `
    background-color: ${theme.palette.action.selected};
  `}

  &:hover {
    color: ${({ theme }) => theme.palette.text.primary};
    background-color: ${({ theme }) => theme.palette.action.hover};

    .menu-item-action {
      opacity: 1;
    }
  }

  &:active {
    .menu-item-action {
      opacity: 1;
    }
  }

  &.Mui-selected {
    color: ${({ theme }) => theme.palette.text.primary};
    background-color: ${({ theme }) => theme.palette.action.selected};
  }
  
  ${({ $isOver, theme }) => $isOver && `
    background-color: ${theme.palette.custom.widgetDropTarget.backgroundColor};
  `}
`

const AnimatedCollapse = animated(Collapse)

function TransitionComponent(props: TransitionProps) {
  const style = useSpring({
    to: {
      opacity: props.in ? 1 : 0,
      transform: `translate3d(0,${props.in ? 0 : 8}px,0)`,
    },
  })

  return <AnimatedCollapse style={style} {...props} />
}
